/**
 * Aggregate fan-in bar for a parent's parallel subagent fan-out.
 *
 * The gap this fills: during a fan-out the only visible truth was N
 * independent RUNNING / Cancel / Retry rows. "How far along is the whole
 * thing" and "is synthesis blocked?" were both unanswerable, so the operator
 * typed "I think you are stuck" into the parent chat to find out.
 *
 * This renders the derived `FanInSnapshot` as one scannable strip:
 * progress fraction, an explicit state chip, the names of the children
 * synthesis is waiting on, and the two actions that end the wait —
 * Synthesize / Force synthesis, and Cancel all children.
 *
 * `aria-live="polite"` on the state chip is deliberate: the states advance on
 * their own as children settle, and a screen-reader user should hear the
 * fan-out become ready without asking for it.
 */
import { html, nothing } from "lit";
import type { ChatSubagentCard } from "../ui-types.ts";
import {
  fanInNeedsAttention,
  fanInPhaseBadgeClass,
  fanInProgressRatio,
  fanInSynthesizeActionLabel,
  type FanInSnapshot,
} from "./fan-in-state.ts";

export type FanInBarProps = {
  snapshot: FanInSnapshot;
  /** Cards backing the snapshot, used only for the "waiting on" tooltips. */
  cards?: ChatSubagentCard[];
  /** Parent session key shown as the owner of this fan-out. */
  parentSessionKey?: string;
  /** Show the parent key line. Off in the chat thread (context is implicit). */
  showParent?: boolean;
  onSynthesize?: () => void;
  onForceSynthesize?: () => void;
  onCancelAll?: () => void;
  /** In-flight flag for the synthesize button. */
  synthesizing?: boolean;
  /** In-flight flag for the cancel-all button. */
  cancelling?: boolean;
  /** Set when a synthesis attempt failed, so the state chip can explain why. */
  error?: string;
};

export function renderFanInBar(props: FanInBarProps) {
  const s = props.snapshot;
  if (s.total === 0) {
    return nothing;
  }

  const ratio = fanInProgressRatio(s);
  const percent = Math.round(ratio * 100);
  const attention = fanInNeedsAttention(s);
  // Gate on real availability, not merely on a handler being wired. Wiring
  // `onSynthesize` must not produce a button that does nothing on a fan-out
  // where every child failed — that reads as a broken control. An in-flight
  // request keeps the control visible so it shows "Synthesizing…" instead of
  // vanishing the moment it is clicked.
  const showSynthesize = Boolean(
    props.synthesizing ||
    (s.canSynthesize && props.onSynthesize) ||
    (s.canForceSynthesize && props.onForceSynthesize),
  );

  return html`
    <section
      class="fan-in-bar fan-in-bar--${s.phase} ${attention ? "fan-in-bar--attention" : ""}"
      data-testid="fan-in-bar"
      data-phase=${s.phase}
      aria-label=${`Fan-out progress: ${s.headline}`}
    >
      <header class="fan-in-bar__head">
        <div class="fan-in-bar__identity">
          <span class="fan-in-bar__label muted">Parallel subagents</span>
          ${
            props.showParent && props.parentSessionKey
              ? html`<span
                  class="fan-in-bar__parent mono muted"
                  data-testid="fan-in-bar__parent"
                  title=${`Parent session: ${props.parentSessionKey}`}
                  >↳ ${props.parentSessionKey}</span
                >`
              : nothing
          }
        </div>
        <span
          class="fleet-map-node__badge fleet-map-node__badge--${fanInPhaseBadgeClass(s.phase)} fan-in-bar__state"
          data-testid="fan-in-bar__state"
          role="status"
          aria-live="polite"
        >
          ${s.stateLabel}
        </span>
      </header>

      <div
        class="fan-in-bar__meter"
        role="progressbar"
        aria-valuenow=${percent}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label=${s.progressLabel}
      >
        <div class="fan-in-bar__meter-fill" style="width:${percent}%"></div>
      </div>

      <p class="fan-in-bar__headline" data-testid="fan-in-bar__headline">${s.headline}</p>

      ${
        s.blocking.length > 0
          ? html`
              <ul class="fan-in-bar__blocking" data-testid="fan-in-bar__blocking">
                ${s.blocking.slice(0, 5).map(
                  (card) => html`
                    <li class="fan-in-bar__blocking-item" title=${card.task || card.label}>
                      <span class="fan-in-bar__blocking-name">${card.label || "unnamed"}</span>
                      <span class="muted">${card.phase ?? card.status}</span>
                    </li>
                  `,
                )}
                ${
                  s.blocking.length > 5
                    ? html`<li class="fan-in-bar__blocking-item muted">
                        +${s.blocking.length - 5} more
                      </li>`
                    : nothing
                }
              </ul>
            `
          : nothing
      }
      ${
        props.error
          ? html`<div class="callout danger fan-in-bar__error" data-testid="fan-in-bar__error">
              ${props.error}
            </div>`
          : nothing
      }

      <div class="fan-in-bar__actions" role="group" aria-label="Fan-out actions">
        ${
          showSynthesize
            ? html`
                <button
                  type="button"
                  class="btn fan-in-bar__synthesize"
                  data-testid="fan-in-bar__synthesize"
                  ?disabled=${props.synthesizing === true}
                  title=${
                    s.canSynthesize
                      ? "Synthesize the completed subagent results"
                      : "Synthesize using only the results that are available"
                  }
                  @click=${() => {
                    if (s.canSynthesize) {
                      props.onSynthesize?.();
                    } else {
                      props.onForceSynthesize?.();
                    }
                  }}
                >
                  ${props.synthesizing ? "Synthesizing…" : fanInSynthesizeActionLabel(s)}
                </button>
              `
            : nothing
        }
        ${
          props.onCancelAll && s.canCancelAll
            ? html`
                <button
                  type="button"
                  class="btn fan-in-bar__cancel-all"
                  data-testid="fan-in-bar__cancel-all"
                  ?disabled=${props.cancelling === true}
                  title="Stop every in-flight subagent in this fan-out"
                  @click=${() => props.onCancelAll?.()}
                >
                  ${props.cancelling ? "Stopping…" : `Cancel all children (${s.outstanding})`}
                </button>
              `
            : nothing
        }
      </div>
    </section>
  `;
}
